import { UzBadge, UzButton, UzDialog, UzInput, UzTextarea } from '@utazzo/ui';
import { Check, ChevronRight, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatInrPaise } from '../invoices.helpers';
import { useInvoice } from '../use-invoices';
import {
  useApproveSupplierChange,
  useBulkApproveSupplierChange,
  useRejectSupplierChange,
  useSupplierChangeApprovals,
} from '../use-supplier-change-approvals';

import type { SupplierChangeApprovalRow } from '../invoices.api';

import { PageShell } from '@/components/page-shell';
import { useSupplierDetail } from '@/features/suppliers/use-suppliers';

/**
 * ADMIN inbox for invoice supplier-change requests (Services app). Requests
 * raised together share a requestGroupId, so rows are grouped by it and a
 * whole group can be approved in one click.
 */

type Status = SupplierChangeApprovalRow['status'];

const STATUS_CHIPS: ReadonlyArray<{ value: Status | ''; label: string }> = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'SUPERSEDED', label: 'Superseded' },
  { value: '', label: 'All' },
];

const REASON_LABEL: Record<SupplierChangeApprovalRow['reason'], string> = {
  MISTAKE: 'Entered by mistake',
  NOT_SERVICEABLE: 'Not serviceable',
};

const pad = (n: number): string => String(n).padStart(2, '0');

/** DD-MM-YYYY HH:mm */
export const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SupplierName = ({ id }: { id: string | null }): React.JSX.Element => {
  const q = useSupplierDetail(id);
  if (!id) return <span className="text-zinc-400">None</span>;
  return <span className="font-medium text-zinc-900">{q.data?.name ?? id.slice(0, 8)}</span>;
};

const InvoiceLabel = ({ id }: { id: string }): React.JSX.Element => {
  const q = useInvoice(id);
  return (
    <Link
      to={`/invoices/${id}`}
      className="text-brand-700 font-medium hover:underline"
      aria-label={`Open invoice ${q.data?.invoiceNumber ?? id}`}
    >
      {q.data?.invoiceNumber ?? id.slice(0, 8)}
    </Link>
  );
};

const statusTone = (s: Status): 'warning' | 'success' | 'destructive' | 'secondary' =>
  s === 'PENDING'
    ? 'warning'
    : s === 'APPROVED'
      ? 'success'
      : s === 'REJECTED'
        ? 'destructive'
        : 'secondary';

export interface RequestGroup {
  key: string;
  invoiceId: string;
  rows: SupplierChangeApprovalRow[];
}

/** Group rows by requestGroupId, preserving first-seen order. */
export const groupByRequest = (rows: ReadonlyArray<SupplierChangeApprovalRow>): RequestGroup[] => {
  const map = new Map<string, RequestGroup>();
  for (const row of rows) {
    const g = map.get(row.requestGroupId);
    if (g) g.rows.push(row);
    else
      map.set(row.requestGroupId, {
        key: row.requestGroupId,
        invoiceId: row.invoiceId,
        rows: [row],
      });
  }
  return [...map.values()];
};

export const SupplierChangeApprovalsPage = (): React.JSX.Element => {
  const [status, setStatus] = useState<Status | ''>('PENDING');
  const [search, setSearch] = useState('');
  const listQuery = useSupplierChangeApprovals(status ? { status } : {});
  const approve = useApproveSupplierChange();
  const reject = useRejectSupplierChange();
  const bulk = useBulkApproveSupplierChange();

  const [rejectTarget, setRejectTarget] = useState<SupplierChangeApprovalRow | null>(null);
  const [remark, setRemark] = useState('');

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = (listQuery.data ?? []).filter(
      (r) =>
        !needle ||
        [r.invoiceId, r.requestGroupId, r.reason, r.requestRemark ?? '', r.requestedById]
          .join(' ')
          .toLowerCase()
          .includes(needle),
    );
    return groupByRequest(rows);
  }, [listQuery.data, search]);

  const closeReject = (): void => {
    setRejectTarget(null);
    setRemark('');
  };

  const submitReject = async (): Promise<void> => {
    if (!rejectTarget || !remark.trim()) return;
    try {
      await reject.mutateAsync({ id: rejectTarget.id, remark: remark.trim() });
      closeReject();
    } catch {
      // Error toast shown by the mutation hook.
    }
  };

  return (
    <PageShell
      title="Supplier change approvals"
      description="Review requests to change or drop a supplier on an issued invoice."
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="w-72">
          <UzInput
            name="search"
            aria-label="Search requests"
            placeholder="Search invoice, group, reason or requester"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Status filter">
          {STATUS_CHIPS.map((c) => (
            <UzButton
              key={c.label}
              size="sm"
              variant={status === c.value ? 'default' : 'outline'}
              onClick={() => setStatus(c.value)}
            >
              {c.label}
            </UzButton>
          ))}
        </div>
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-zinc-500">Loading...</p>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600">
          Failed to load requests.{' '}
          <button className="underline" onClick={() => void listQuery.refetch()}>
            Retry
          </button>
        </p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-zinc-500">No supplier change requests match.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const pending = g.rows.filter((r) => r.status === 'PENDING');
            return (
              <section
                key={g.key}
                data-testid={`group-${g.key}`}
                className="rounded-lg border border-zinc-200 bg-white"
              >
                <header className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-zinc-500">Invoice</span>
                    <InvoiceLabel id={g.invoiceId} />
                    <ChevronRight className="size-4 text-zinc-400" aria-hidden />
                    <span className="text-zinc-500">
                      {g.rows.length} request{g.rows.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {pending.length > 1 ? (
                    <UzButton
                      size="sm"
                      onClick={() =>
                        void bulk.mutateAsync({ requestGroupId: g.key }).catch(() => undefined)
                      }
                      loading={bulk.isPending}
                    >
                      <Check className="size-4" /> Approve all ({pending.length})
                    </UzButton>
                  ) : null}
                </header>
                <ul className="divide-y divide-zinc-100">
                  {g.rows.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <SupplierName id={r.oldSupplierId} />
                          <span aria-hidden>{'->'}</span>
                          <SupplierName id={r.newSupplierId} />
                          <UzBadge tone={statusTone(r.status)}>{r.status}</UzBadge>
                        </div>
                        <div className="text-xs text-zinc-500">
                          {REASON_LABEL[r.reason]} | Settled{' '}
                          {formatInrPaise(r.settledSnapshotPaise)} | Payable{' '}
                          {formatInrPaise(r.payableSnapshotPaise)}
                        </div>
                        <div className="text-xs text-zinc-500">
                          Requested {formatWhen(r.createdAt)}
                          {r.requestRemark ? ` | ${r.requestRemark}` : ''}
                          {r.reviewRemark ? ` | Review: ${r.reviewRemark}` : ''}
                        </div>
                      </div>
                      {r.status === 'PENDING' ? (
                        <div className="flex gap-2">
                          <UzButton
                            size="sm"
                            onClick={() =>
                              void approve.mutateAsync({ id: r.id }).catch(() => undefined)
                            }
                            loading={approve.isPending && approve.variables?.id === r.id}
                          >
                            <Check className="size-4" /> Approve
                          </UzButton>
                          <UzButton size="sm" variant="outline" onClick={() => setRejectTarget(r)}>
                            <X className="size-4" /> Reject
                          </UzButton>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <UzDialog
        open={rejectTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeReject();
        }}
        title="Reject supplier change"
        description="Rejecting leaves the invoice exactly as it is. A remark is required so the requester knows why."
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={closeReject} disabled={reject.isPending}>
              Cancel
            </UzButton>
            <UzButton
              variant="outline"
              onClick={() => void submitReject()}
              loading={reject.isPending}
              disabled={!remark.trim()}
            >
              Confirm rejection
            </UzButton>
          </div>
        }
      >
        <UzTextarea
          name="reviewRemark"
          label="Reason for rejection (required)"
          rows={3}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          disabled={reject.isPending}
        />
      </UzDialog>
    </PageShell>
  );
};
